import {
    ActivityFeedModelType,
    OrderStatusFilterOptions,
    QuoteStatusFilterOptions,
} from '../../../server/types/customer-activity-feed-types.ts';
import type { ActivityQueryFilters } from '../../activity-feed/hooks/use-activity-filters.ts';

export type ActivityFeedPreset = 'openQuotes' | 'openOrders';

/**
 * The filters a preset sets. "Open" matches `getOpenTransactionSummary`, so the
 * count on the preset is what the feed lists once it is on.
 */
const presetFilters: Record<ActivityFeedPreset, ActivityQueryFilters> = {
    openQuotes: {
        modelTypes: [ActivityFeedModelType.QUOTE],
        quoteStatuses: [QuoteStatusFilterOptions.OPEN],
    },
    openOrders: {
        modelTypes: [ActivityFeedModelType.ORDER],
        orderStatuses: [OrderStatusFilterOptions.OPEN, OrderStatusFilterOptions.OPEN_IN_PICKING],
    },
};

function sameValues(a: string[] | undefined, b: string[] | undefined): boolean {
    const left = a ?? [];
    const right = b ?? [];
    return left.length === right.length && left.every((value) => right.includes(value));
}

function getModelTypes(filters: ActivityQueryFilters): string[] | undefined {
    if (filters.modelTypes?.length) {
        return filters.modelTypes;
    }

    // a single type from an old link (the old stats bar) counts as that one type
    return filters.modelType ? [filters.modelType] : undefined;
}

/**
 * The preset the applied filters spell out exactly. Changing either of its
 * filters in the menu turns it off.
 */
export function getActivePreset(filters: ActivityQueryFilters): ActivityFeedPreset | undefined {
    const modelTypes = getModelTypes(filters);
    const matches = (preset: ActivityQueryFilters): boolean =>
        sameValues(modelTypes, preset.modelTypes) &&
        sameValues(filters.quoteStatuses, preset.quoteStatuses) &&
        sameValues(filters.orderStatuses, preset.orderStatuses);

    if (matches(presetFilters.openQuotes)) {
        return 'openQuotes';
    }

    if (matches(presetFilters.openOrders)) {
        return 'openOrders';
    }

    return undefined;
}

/**
 * The filters with the type and quote/order statuses swapped for `preset`'s, or
 * cleared when there is none. Everything else is kept.
 */
export function withPreset(
    filters: ActivityQueryFilters,
    preset: ActivityFeedPreset | undefined,
): ActivityQueryFilters {
    const cleared: ActivityQueryFilters = {
        ...filters,
        modelType: undefined,
        modelTypes: undefined,
        quoteStatuses: undefined,
        orderStatuses: undefined,
    };

    return preset ? { ...cleared, ...presetFilters[preset] } : cleared;
}
